import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    SxProps,
    Theme,
    Card,
    CardContent,
    CardActions,
    Button,
} from "@mui/material";
import { CardActionArea } from "@mui/material";
import { BlogArticlePreview } from "../adapters/api";
import { useIsMobile } from "../hooks";
import LearnMoreIcon from "../components/icons/LearnMoreIcon";
import { useTranslation } from "react-i18next";

const BlogArticleCardStyle: Record<string, SxProps<Theme>> = {
    cardContainer: (theme: Theme) => ({
        background: "transparent",
        border: "3px solid",
        borderColor: theme.palette.secondary.main,
        borderRadius: "10px",
        padding: "5px",
        maxHeight: "250px",
        height: "250px",
    }),
    cardContainerMobile: (theme: Theme) => ({
        background: "transparent",
        border: "1px solid",
        borderColor: theme.palette.secondary.main,
        borderRadius: "10px",
        padding: "5px",
    }),
    cardContent: {
        height: "100%",
        padding: "0px",
    },
    cardDate: {
        textAlign: "left",
        color: "#BABABA",
        fontSize: "1rem",
        marginLeft: "10px",
    },
    cardDateMobile: {
        textAlign: "left",
        color: "#BABABA",
        fontSize: "16px",
        marginLeft: "10px",
    },
    cardImage: {
        height: "100px",
        margin: "10px",
        img: {
            height: "100%",
            width: "100%",
            objectFit: "cover",
            borderRadius: "20px",
        },
    },
    cardTitle: {
        fontSize: "1.2rem",
        textAlign: "left",
        marginLeft: "10px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: "2",
        WebkitBoxOrient: "vertical",
    },
    cardTitleMobile: {
        fontSize: "18px",
        textAlign: "left",
        marginLeft: "10px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: "2",
        WebkitBoxOrient: "vertical",
    },
    cardButton: {
        color: "white",
        marginLeft: "0px !important",
    },
    cardHovered: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "#353535BA",
        padding: "10px",
    },
    cardHoveredText: {
        textAlign: "left",
        fontSize: "1.2rem",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    cardHoveredTextMobile: {
        textAlign: "left",
        fontSize: "1.2rem",
        overflow: "hidden",
    },
    cardDescriptionText: {
        textAlign: "left",
        fontSize: "16px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        marginLeft: "10px",
        marginTop: "10px",
    },
};

export type BlogArticleCardProps = {
    item: BlogArticlePreview;
    onLearnMore: () => void;
};

const BlogArticleCard: React.FC<BlogArticleCardProps> = ({
    item,
    onLearnMore,
}) => {
    const { isMobile } = useIsMobile();
    const [hovered, setHovered] = useState(false);
    const [t] = useTranslation();
    return (
        <Card
            sx={
                isMobile
                    ? BlogArticleCardStyle.cardContainerMobile
                    : BlogArticleCardStyle.cardContainer
            }
        >
            <CardActionArea
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onClick={onLearnMore}
                sx={{ height: "100%" }}
            >
                <CardContent sx={BlogArticleCardStyle.cardContent}>
                    {hovered && !isMobile && (
                        <Box sx={BlogArticleCardStyle.cardHovered}>
                            <Typography sx={BlogArticleCardStyle.cardHoveredText}>
                                {item.hoverText}
                            </Typography>
                            <CardActions
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-end",
                                }}
                            >
                                <Button
                                    sx={BlogArticleCardStyle.cardButton}
                                    size="small"
                                    endIcon={<LearnMoreIcon />}
                                    onClick={onLearnMore}
                                >
                                    {t("blog.keepreading")}
                                </Button>
                            </CardActions>
                        </Box>
                    )}
                    <Box sx={BlogArticleCardStyle.cardImage}>
                        <img src={"/backend/static/" + item.thumbnail} />
                    </Box>
                    <Typography
                        sx={
                            isMobile
                                ? BlogArticleCardStyle.cardDateMobile
                                : BlogArticleCardStyle.cardDate
                        }
                    >
                        {item.createdDate}
                    </Typography>
                    <Typography
                        sx={
                            isMobile
                                ? BlogArticleCardStyle.cardTitleMobile
                                : BlogArticleCardStyle.cardTitle
                        }
                    >
                        {item.title}
                    </Typography>
                    {isMobile && (
                        <Box sx={BlogArticleCardStyle.cardDescription}>
                            <Typography sx={BlogArticleCardStyle.cardDescriptionText}>
                                {item.hoverText}
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </CardActionArea>
        </Card>
    );
};

export default BlogArticleCard;
